import _ from 'lodash';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  elementToCalculateStripManagementThumbnailInfo,
  setStripDeviceThumbnailInfo,
  DEVICE_TAB_TYPES,
  SIZE_DEVICE_STRIP_MANAGEMENT_THUMBNAIL,
} from '@/shared/config';
import {
  selectChangeStripData,
  selectIsLoadingStrip,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import { VALID_DEVICE_EVENT_STATUS_OPTIONS } from '@/widgets/strips-management/ui/constants';
import {
  ID_DIV_DEVICE_STRIP_MANAGEMENT,
  checkStatusStripChange,
} from '@/widgets/strips-management/ui/handler';
import getUpdateValue from './handler';
import type { StripsDeviceActionButtonProps } from './stripsDeviceActionButton';

/**
 * Backs the device strip action bar, applying include, validity and thumbnail-size changes to the current selection. Recomputes the grid layout when the thumbnail size changes, merges the selected and multi-selected strips with their pending changes to derive the checkbox, indeterminate and validity states, and routes every edit through checkStatusStripChange into the store's change list.
 *
 * @param props - Action bar props with the tab store key and the active sub-tab.
 * @returns An object carrying the tab store key, the loading flag, the thumbnail size, the force-close-menu toggle, the derived config info and the size, checkbox, select and reset handlers.
 */
const useStripsDeviceActionButton = ({
  keyRecoil,
  activeButton,
}: Pick<StripsDeviceActionButtonProps, 'keyRecoil' | 'activeButton'>) => {
  const wrapperHeight = useRef<any>();
  const sizeThumbnailConfig = useReportStore((s) => s.sizeStripDeviceThumbnailConfig);
  const setSizeThumbnailConfig = useReportStore((s) => s.actions.setSizeStripDeviceThumbnailConfig);
  const selectedStrip: any = useReportStore(selectSelectedStrip(keyRecoil));
  const selectedMultipleStrip: any = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const setChangeStripDataAction = useReportStore((s) => s.actions.setChangeStripData);
  /**
   * Writes the pending strip change list for this tab into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New change list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setChangeStripData = (v: Parameters<typeof setChangeStripDataAction>[1]) =>
    setChangeStripDataAction(keyRecoil, v);
  const isDeviceEvent = useMemo(() => DEVICE_TAB_TYPES.includes(activeButton), [activeButton]);
  const [size, setSize] = useState(sizeThumbnailConfig?.name);
  const [forceCloseMenu, setForceCloseMenu] = useState(false);
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));

  /**
   * Works out how many strip thumbnails of a given size fit the device strip grid. Measures the grid wrapper height, subtracts the fixed left column, padding and gaps from the viewport width and from that height, and stores the resulting strips-per-row and row count.
   *
   * @param width - Thumbnail width to fit across a row.
   * @param height - Thumbnail height to fit down a column.
   * @returns Nothing.
   */
  const calculateStripThumbnailInfo = ({ width, height }: any) => {
    const stripWrapperEl = document.getElementById(ID_DIV_DEVICE_STRIP_MANAGEMENT);
    if (stripWrapperEl) {
      if (stripWrapperEl.offsetHeight) {
        wrapperHeight.current = stripWrapperEl.offsetHeight;
      }
      const { leftColumnWidth, paddingBothSide, stripBeatGap, paddingTopBottom } =
        elementToCalculateStripManagementThumbnailInfo;
      const stripPerRow = Math.floor(
        (window.innerWidth - leftColumnWidth - paddingBothSide + stripBeatGap) /
          (width + stripBeatGap),
      );
      const stripRow = Math.floor(
        (wrapperHeight.current - paddingTopBottom + stripBeatGap) / (height + stripBeatGap),
      );
      setStripDeviceThumbnailInfo({ stripPerRow, stripRow });
    }
  };

  /**
   * Switches the device strip grid to a different thumbnail size. Ignores a pick matching the current size, otherwise recomputes the rows and columns for the new dimensions and stores the size config on a short delay so the layout settles first.
   *
   * @param item - Menu item whose props value names the chosen size.
   * @returns Nothing.
   */
  const onClickItemSize = ({ item }: any) => {
    setSize(item.props.value);
    const size = item.props.value;
    if (size !== sizeThumbnailConfig.name) {
      //* Change from current tab
      const newSizeThumbnail = (SIZE_DEVICE_STRIP_MANAGEMENT_THUMBNAIL as any)[size];
      calculateStripThumbnailInfo({
        width: newSizeThumbnail.width,
        height: newSizeThumbnail.height,
      });
      setTimeout(() => {
        setSizeThumbnailConfig(newSizeThumbnail);
      }, 10);
    }
  };

  /**
   * Toggles an include flag across the selected device strips. Merges the selected and multi-selected strips with their pending changes, derives the new values with getUpdateValue so the strip flag stays consistent with the report flag, and folds the result into the store's change list; an empty selection is ignored.
   *
   * @param name - Include field being toggled.
   * @param value - New checked value for that field.
   * @returns Nothing.
   */
  const onChangeCheckbox = (name: any, value: any) => {
    const willUpdateList = _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id');
    if (_.isEmpty(willUpdateList)) {
      return;
    }

    const mergeChanges = _.map(willUpdateList, (x) => {
      const change = _.find(changeStripData, (y) => y.id === x.id);
      if (change) {
        return {
          ...x,
          ...change,
        };
      }
      return { ...x, ...(x.originalInfo || {}) };
    });
    const updatedList = _.map(_.uniqBy(mergeChanges, 'id'), (x) => ({
      ...x,
      ...getUpdateValue(name, value, x.isIncludedToReport),
    }));
    const changesData = checkStatusStripChange(updatedList, changeStripData);
    setChangeStripData(changesData);
  };

  /**
   * Merges a partial update into every selected device strip. Combines the selected and multi-selected strips with their pending changes or original values, applies the patch to each, and rewrites the store's change list through checkStatusStripChange.
   *
   * @param updateData - Partial strip fields to apply to each selected strip.
   * @returns Nothing.
   */
  const updateFunc = (updateData: any) => {
    const willUpdateList = _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id');
    const mergeChanges = _.map(willUpdateList, (x) => {
      const change = _.find(changeStripData, (y) => y.id === x.id);
      if (change) {
        return {
          ...x,
          ...change,
        };
      }
      return { ...x, ...(x.originalInfo || {}) };
    });
    const updatedList = _.map(mergeChanges, (x) => ({ ...x, ...updateData }));
    const changesData = checkStatusStripChange(updatedList, changeStripData);
    setChangeStripData(changesData);
  };

  /**
   * Applies a dropdown choice, such as event validity, to the selected device strips. Passes the field and the option value into the shared update path that merges pending changes and rewrites the change list.
   *
   * @param name - Field the dropdown edits.
   * @param option - Chosen option whose value is written to that field.
   * @returns Nothing.
   */
  const onChangeSelect = (name: any, option: any) => {
    updateFunc({ [name]: option.value });
  };

  /**
   * Reverts the selected device strips to their original values. Restores each strip's originalInfo, clears its update status, and folds the result back into the store's change list.
   *
   * @returns Nothing.
   */
  const onClickReset = () => {
    const willUpdateList = _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id');
    const updatedList = _.map(willUpdateList, (x) => ({
      ...x,
      ...x.originalInfo,
      updateStatus: '',
    }));
    const changesData = checkStatusStripChange(updatedList, changeStripData);
    setChangeStripData(changesData);
  };

  useLayoutEffect(() => {
    const { width, height } = sizeThumbnailConfig;
    calculateStripThumbnailInfo({ width, height });
  }, []);

  const configInfo: any = useMemo(() => {
    if (!_.isEmpty(selectedMultipleStrip)) {
      const mergeChanges = _.map(
        _.uniqBy([...(selectedMultipleStrip || []), selectedStrip || {}], 'id'),
        (x) => {
          const change = _.find(changeStripData, (y) => y?.id === x?.id);
          if (change) return change;
          return { ...x, ...x.originalInfo };
        },
      );
      const total = mergeChanges.length;
      const countIncludedStrip = _.filter(mergeChanges, (x) => x.isIncludedStrip).length;
      const countIncludedToReport = _.filter(mergeChanges, (x) => x.isIncludedToReport).length;
      const isNoEcgData = !_.isEmpty(_.filter(mergeChanges, (x) => x.isNoEcgData));
      const carriedBySentReport = !_.isEmpty(_.filter(mergeChanges, (x) => x.carriedBySentReport));
      const data = {
        isIncludedStrip: countIncludedStrip === countIncludedToReport,
        isIncludedToReport: countIncludedToReport === total,
        isIndeterminateStrip: countIncludedStrip > 0 && countIncludedStrip < countIncludedToReport,
        isIndeterminateToReport: countIncludedToReport > 0 && countIncludedToReport < total,
        isDisabledStrip: countIncludedToReport === 0,
        foundEventChange: _.some(mergeChanges, (x) => x.updateStatus),
        carriedBySentReport,
        isValid: _.find(
          VALID_DEVICE_EVENT_STATUS_OPTIONS,
          (x) => x.value === !!_.some(mergeChanges, (x) => x.isValid),
        ),
        isNoEcgData,
      };
      if (isDeviceEvent) {
        const everyValid = _.every(mergeChanges, (x) => x.isValid);
        const someValid = _.some(mergeChanges, (x) => x.isValid);
        _.assign(data, {
          isValid: everyValid
            ? _.find(VALID_DEVICE_EVENT_STATUS_OPTIONS, (x) => x.value === true)
            : someValid
              ? { label: 'Multiple', value: undefined }
              : _.find(VALID_DEVICE_EVENT_STATUS_OPTIONS, (x) => x.value === false),
        });
      }

      return data;
    }
    if (isDeviceEvent) {
      setForceCloseMenu((prev) => !prev);
    }

    if (!_.isEmpty(selectedStrip)) {
      const changeSelectedStrip = _.find(changeStripData, (x) => x.id === selectedStrip.id);
      if (changeSelectedStrip) {
        return {
          isIncludedStrip:
            changeSelectedStrip.isIncludedToReport && changeSelectedStrip.isIncludedStrip,
          isIncludedToReport: changeSelectedStrip.isIncludedToReport,
          isDisabledStrip: !changeSelectedStrip.isIncludedToReport,
          isValid: _.find(
            VALID_DEVICE_EVENT_STATUS_OPTIONS,
            (x) => x.value === changeSelectedStrip.isValid,
          ),
          foundEventChange: changeSelectedStrip.updateStatus,
          isNoEcgData: changeSelectedStrip.isNoEcgData,
          carriedBySentReport: changeSelectedStrip.carriedBySentReport,
        };
      }
      const { originalInfo } = selectedStrip;
      return {
        isIncludedStrip: originalInfo.isIncludedToReport && originalInfo.isIncludedStrip,
        isIncludedToReport: originalInfo.isIncludedToReport,
        isDisabledStrip: !originalInfo.isIncludedToReport,
        isValid: _.find(VALID_DEVICE_EVENT_STATUS_OPTIONS, (x) => x.value === originalInfo.isValid),
        isNoEcgData: originalInfo.isNoEcgData,
        carriedBySentReport: originalInfo.carriedBySentReport,
      };
    }
    return { isDisabled: true };
  }, [selectedMultipleStrip, selectedStrip, changeStripData, isDeviceEvent]);

  return {
    keyRecoil,
    isLoadingStrip,
    size,
    forceCloseMenu,
    configInfo,
    onClickItemSize,
    onChangeCheckbox,
    onChangeSelect,
    onClickReset,
  };
};

export default useStripsDeviceActionButton;
